import {
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  Column,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  matchString,
  Table,
  TableOptions,
  Tooltip,
  WarningInfoIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { TableRowImage } from '@/components/TableRowImage';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { groupItemsById } from '@/features/broadcast-schedule-program/helpers/group-by-id';
import { TSegmentDeclaration } from '@/features/segments/types';
import { usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

import { BroadcastScheduleProgramBodySegmentToolkitDeclarationsSubTable } from './BroadcastScheduleProgramBodySegmentToolkitDeclarationsSubTable';

type TBroadcastScheduleTableProps = {
  items: TSegmentDeclaration[];
  onPreview?: (item: TSegmentDeclaration) => void;
  onUpdate?: (item: TSegmentDeclaration) => void;
  onDelete?: (item: TSegmentDeclaration) => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitDeclarationsTable({
  items,
  onPreview,
  onUpdate,
  onDelete,
}: TBroadcastScheduleTableProps) {
  const { activeSegment } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { byId: peopleById } = usePeopleById(
    items.map(item => item.people_id),
    { keepPrevious: true }
  );

  const groupedPeople = useMemo(() => groupItemsById({ items, idKey: 'people_id' }), [items]);

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    filters: [],
    searchBarPlaceholder: t('Buscar por persona'),
  };

  const columns: Column<TSegmentDeclaration>[] = useMemo(
    () => [
      {
        id: 'persona',
        title: t('Persona'),
        searchable: true,
        matchFn: (query, item) => {
          let label = item?.entity_label;
          if (!label && (item as any)?.children?.length) {
            label = (item as any)?.children[0].entity_label;
          }
          return matchString((label ?? '').toString(), query);
        },
        render: item => {
          const selectedID = item.people_id || item.people_id || item.id;
          const people = peopleById.get(selectedID);
          const storedLabel = item.entity_label || (item as any)?.children?.[0]?.entity_label || '';
          return (
            <TableRowImage
              source={people?.parameters?.main_logo_presigned_url || '/default-logo.svg' || ''}
              alt={people?.name || storedLabel}
              name={people?.name || storedLabel}
            />
          );
        },
      },
      {
        id: 'declaracion',
        title: t('Declaración'),
        render: item => {
          return item?.children && item.children?.length > 0 ? (
            <Badge variant={BadgeVariantEnum.DefaultFilled}>{item.children.length}</Badge>
          ) : (
            <Tooltip help={item.text || ''}>
              <TableRow content={item.text || ''} />
            </Tooltip>
          );
        },
      },
      {
        id: 'timeline',
        title: t('Timeline'),
        render: item => {
          if (!activeSegment || !item.start_datetime) return <></>;

          const seconds = getOffsetHHMMSS(item.start_datetime, activeSegment.start_datetime);

          return item?.children && item.children?.length > 0 ? (
            <Badge variant={BadgeVariantEnum.DefaultFilled}>{item.children.length}</Badge>
          ) : (
            <TableRow content={seconds} />
          );
        },
      },
      {
        id: 'fiable',
        title: t('Revisado'),
        render: item => {
          return item?.children && item.children?.length > 0 ? (
            <>
              {item.children.some(child => child.review_state === 0) ? (
                <WarningInfoIcon className="text-feedback-error-20" />
              ) : null}
            </>
          ) : item.review_state === 1 ? (
            <TableRow className="!font-semibold !text-feedback-success-30" content={t('Si')} />
          ) : (
            <TableRow className="!font-semibold !text-feedback-warning-30" content={t('No')} />
          );
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: (item, _index, _rows, showSubArrows, subArrowsAreVisible) => (
          <div className="inline-flex w-full justify-end">
            {item?.children && item.children?.length > 0 ? (
              <>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton
                  color={IconButtonColorEnum.NONE_DEFAULT}
                  shape={IconButtonShapeEnumOld.OLD_SQUARED}
                  onClick={() => showSubArrows(prevVal => !prevVal)}
                >
                  {!subArrowsAreVisible ? (
                    <ChevronDownIcon size={IconSizeEnum.XS} />
                  ) : (
                    <ChevronUpIcon size={IconSizeEnum.XS} />
                  )}
                </IconButton>
              </>
            ) : (
              <RenderActionButtons
                item={item}
                onPreview={() => onPreview?.(item)}
                onUpdate={() => onUpdate?.(item)}
                onDelete={() => onDelete?.(item)}
              />
            )}
          </div>
        ),
      },
    ],
    [t, items, peopleById]
  );

  return (
    <Table<any>
      options={options}
      columns={columns}
      data={groupedPeople}
      renderAdditionalRow={item => (
        <BroadcastScheduleProgramBodySegmentToolkitDeclarationsSubTable
          item={item}
          columns={columns}
          onPreview={onPreview}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      )}
      ignoreWrap
    />
  );
}
